/**
 * @file SettingsToc.tsx
 * @description Оглавление групп настроек панели (phase19.16, SB122;
 * uiux-settings-groups §2): строка пилюль как у `FilterChips` (48 на
 * касании, 32 с 1024), подписи `tocLabel`. Ряд не переносится — прокрутка
 * вбок с затуханием краёв и сдвигом к активной (общие хуки с `Tabs`).
 *
 * Нажатие: раскрыть группу → прокрутить её заголовок под липкую строку →
 * фокус на заголовок. Активная — `aria-current="location"`, по
 * `IntersectionObserver` (`useActiveGroup`). Группа с ошибкой — красная
 * точка. От двух свёрнутых — «Развернуть все». Показывает корень
 * `SettingsGroups` (от 4 групп и от 768) внутри `PanelStickyTop`.
 */

import type { MouseEvent } from "react";

import { CHIP_BASE, CHIP_CLASS } from "../FilterChips";
import { useRevealActive } from "../useRevealActive";
import { useScrollFade } from "../useScrollFade";
import type { SettingsGroupsApi } from "./context";
import { useActiveGroup } from "./useActiveGroup";
import { cn } from "@/lib/utils";

/** Оглавление получает состояние групп от корня (`SettingsGroups`). */
export function SettingsToc({ api }: { api: SettingsGroupsApi }) {
  const fade = useScrollFade<HTMLDivElement>();
  const { groups } = api;
  const activeIndex = groups.findIndex((g) => g.id === api.active);
  useRevealActive(fade.ref, activeIndex, "[data-toc-item]", fade.update);
  useActiveGroup(api, true);
  const collapsed = groups.filter((g) => !api.isOpen(g.id)).length;

  function go(event: MouseEvent<HTMLAnchorElement>, id: string) {
    event.preventDefault();
    api.openGroup(id, { focus: true, flash: false });
  }

  return (
    // Перенос: если пилюлям остаётся меньше 240 px, «Развернуть все» уходит строкой ниже,
    // а не накрывает оглавление (QA SB120 этап 1, D10).
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 py-2">
      <nav aria-label="Группы настроек" className="min-w-0 flex-1 basis-60">
        <div ref={fade.ref} onScroll={fade.update} style={fade.style} className="flex gap-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none]">
          {groups.map((g) => {
            const active = g.id === api.active;
            const label = g.tocLabel ?? g.title;
            return (
              <a
                key={g.id}
                href={`#group-${g.id}`}
                data-toc-item=""
                aria-current={active ? "location" : undefined}
                title={label === g.title ? undefined : g.title}
                onClick={(event) => go(event, g.id)}
                className={cn(CHIP_BASE, active ? CHIP_CLASS.active : CHIP_CLASS.idle)}
              >
                {label}
                {g.status === "error" ? (
                  <span role="img" aria-label="нужно исправить" className="inline-block size-2 shrink-0 rounded-full bg-destructive" />
                ) : null}
              </a>
            );
          })}
        </div>
      </nav>
      {collapsed >= 2 ? (
        <button
          type="button"
          onClick={api.expandAll}
          className="type-caption ml-auto inline-flex min-h-[var(--chip-h,3rem)] shrink-0 items-center rounded-md px-1 font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          Развернуть все
        </button>
      ) : null}
    </div>
  );
}
